import { useState } from "react";
import AdminLayout from "../layouts/AdminLayout";
import { hasPermission } from "../utils/permissions";

function AdminAddProduct() {
  const [form, setForm] = useState({
    title: "",
    price: "",
    category: "",
    subcategory: "",
    colors: "",
    sizes: [],
    description: "",
    offer: "",
    tags: [],
    purchaseOptions: [],
    status: "published"
  });

  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [message, setMessage] = useState("");

  if (!hasPermission("products", "edit")) {
    return (
      <AdminLayout>
        <p style={{ padding: "20px", color: "red", fontWeight: "bold" }}>
          ❌ You do not have permission to add products.
        </p>
      </AdminLayout>
    );
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setForm((prev) => ({
        ...prev,
        [name]: checked
          ? [...prev[name], value]
          : prev[name].filter((v) => v !== value)
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e, status = "published") => {
    e.preventDefault();

    if (!form.title || !form.price || !form.category || !form.subcategory) {
      alert("Please fill all required fields.");
      return;
    }

    const formData = new FormData();
    Object.keys(form).forEach((key) => {
      const val = form[key];
      formData.append(key, Array.isArray(val) ? JSON.stringify(val) : val);
    });
    formData.append("status", status);
    formData.append("image", image);

    const res = await fetch("https://kolibusiness.com/backend/add-product.php", {
      method: "POST",
      body: formData
    });

    const result = await res.json();
    if (result.success) {
      setMessage("✅ Product added successfully!");
      setForm({
        title: "",
        price: "",
        category: "",
        subcategory: "",
        colors: "",
        sizes: [],
        description: "",
        offer: "",
        tags: [],
        purchaseOptions: [],
        status: "published"
      });
      setImage(null);
      setPreview(null);
    } else {
      setMessage("❌ Failed to add product: " + result.message);
    }
  };

  return (
    <AdminLayout>
      <h2 style={styles.heading}>🛒 Add New Product</h2>
      <form onSubmit={(e) => handleSubmit(e)} style={styles.form}>
        {/* CATEGORY */}
        <div style={styles.section}>
          <label>📂 Category</label>
          <select name="category" value={form.category} onChange={handleChange} required>
            <option value="">Select Category</option>
            <option value="Clothes">Clothes</option>
            <option value="Electronics">Electronics</option>
            <option value="Furniture">Furniture</option>
          </select>

          <label>👤 Subcategory</label>
          <select name="subcategory" value={form.subcategory} onChange={handleChange} required>
            <option value="">Select Subcategory</option>
            <option value="Men">Men</option>
            <option value="Women">Women</option>
            <option value="Kids">Kids</option>
          </select>
        </div>

        {/* DETAILS */}
        <div style={styles.section}>
          <label>📝 Title</label>
          <input type="text" name="title" value={form.title} onChange={handleChange} required />

          <label>💸 Price</label>
          <input type="number" name="price" value={form.price} onChange={handleChange} required />

          <label>🎨 Colors</label>
          <input type="text" name="colors" value={form.colors} onChange={handleChange} placeholder="e.g., Red, Blue" />

          <label>📏 Sizes</label>
          <div style={styles.checkboxGroup}>
            {["S", "M", "L", "XL", "XXL"].map((size) => (
              <label key={size}>
                <input
                  type="checkbox"
                  name="sizes"
                  value={size}
                  checked={form.sizes.includes(size)}
                  onChange={handleChange}
                />{" "}
                {size}
              </label>
            ))}
          </div>

          <label>📄 Description</label>
          <textarea name="description" value={form.description} onChange={handleChange} required />

          <label>🏷️ Offer</label>
          <input type="text" name="offer" value={form.offer} onChange={handleChange} placeholder="e.g., 10% off" />
        </div>

        {/* OPTIONS */}
        <div style={styles.section}>
          <label>🔥 Tags</label>
          <div style={styles.checkboxGroup}>
            {["Hot", "Similar", "New"].map((tag) => (
              <label key={tag}>
                <input
                  type="checkbox"
                  name="tags"
                  value={tag}
                  checked={form.tags.includes(tag)}
                  onChange={handleChange}
                />{" "}
                {tag}
              </label>
            ))}
          </div>

          <label>🛒 Purchase Options</label>
          <div style={styles.checkboxGroup}>
            {["Add to Cart", "Buy Now", "Cash on Delivery", "EMI"].map((option) => (
              <label key={option}>
                <input
                  type="checkbox"
                  name="purchaseOptions"
                  value={option}
                  checked={form.purchaseOptions.includes(option)}
                  onChange={handleChange}
                />{" "}
                {option}
              </label>
            ))}
          </div>
        </div>

        {/* IMAGE */}
        <div style={styles.section}>
          <label>🖼️ Product Image</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              setImage(e.target.files[0]);
              setPreview(URL.createObjectURL(e.target.files[0]));
            }}
            required
          />
          {preview && <img src={preview} alt="Preview" style={styles.preview} />}
        </div>

        <div style={styles.buttonRow}>
          <button type="submit" style={styles.submit}>🚀 Submit Product</button>
          <button type="button" onClick={(e) => handleSubmit(e, "draft")} style={styles.draft}>💾 Save as Draft</button>
        </div>

        {message && <p style={styles.message}>{message}</p>}
      </form>
    </AdminLayout>
  );
}

const styles = {
  heading: {
    fontSize: "28px",
    fontWeight: "600",
    marginBottom: "20px"
  },
  form: {
    background: "#ffffff",
    padding: "25px",
    borderRadius: "12px",
    maxWidth: "700px",
    boxShadow: "0 0 20px rgba(0, 0, 0, 0.08)",
    display: "flex",
    flexDirection: "column",
    gap: "20px"
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: "10px"
  },
  checkboxGroup: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap"
  },
  buttonRow: {
    display: "flex",
    gap: "12px"
  },
  submit: {
    background: "linear-gradient(135deg, #00b894, #0984e3)",
    color: "#fff",
    padding: "12px 20px",
    fontSize: "16px",
    fontWeight: "bold",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer"
  },
  draft: {
    background: "#636e72",
    color: "#fff",
    padding: "12px 20px",
    fontSize: "16px",
    fontWeight: "bold",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer"
  },
  preview: {
    width: "140px",
    marginTop: "10px",
    borderRadius: "6px"
  },
  message: {
    color: "green",
    fontWeight: "bold",
    marginTop: "15px"
  }
};

export default AdminAddProduct;
